<template>
  <v-layout class="mx-4" column id="treatment">
    <mrx-triage></mrx-triage>
    <v-card flat class="treatment-instructions">
      <mrx-translation class="mb-2"
        token="app.treatments.instructions.title"></mrx-translation>
    </v-card>
    <template
      v-for="triage in triages"
      >
      <template
        v-for="assessment in getAssessmentsWithTriage(triage)">
        <post-assessment-card
          v-if="getTreatmentsForAssessment(assessment.name).length > 0"
          :headerToken="riskAssessmentToken(assessment.name, assessment.severity)"
          :headerIcon="icon(triage).icon"
          :headerIconColor="icon(triage).color"
          :key="`${assessment.name}_${assessment.severity}`"
          :contentList="formatTreatments(assessment.name)"
          :isChecklist="true"
          :disabledItems="disabled"
          v-model="selected"
          @handleInput="setSelected"
          >
        </post-assessment-card>
      </template>
    </template>
  </v-layout>
</template>

<script>
  import mrx from '@/mixins/mrx'
  import icons from '@/mixins/icons'
  import languageLogic from '@/mixins/languageLogic'
  import PostAssessmentCard from '../layouts/PostAssessmentCard.vue'
  import { mapGetters } from 'vuex'

  export default {
    components: { PostAssessmentCard },
    name: 'Treatment',
    mixins: [languageLogic, mrx, icons],
    data() {
      return {
        selected: [],
        disabled: {}
      }
    },
    computed: {
      ...mapGetters('v4/mrx', {
        treatments: 'treatments',
        plans: 'plans',
        assessments: 'assessments',
        triageSelected: 'triageSelected',
      }),
    },
    mounted() {
      window.scroll(0, 0)
      this.selected = this.plans().filter(b => b.selected).map(p => p.name)
      this.updateDisabledItems();  // Ensure the disabled items are initialized
    },
    watch: {
      selected: function(val) {
        this.$store.commit('v4/mrx/select', { type: 'plan', data: this.selected })
      },
    },
    methods: {
      getAssessmentsWithTriage(triage) {
        const noAssessmentsSelected = this.triageSelected() === 'none_selected'
        return this.assessments().filter(a => {
          return a.triage_recommendation === triage && (a.selected || noAssessmentsSelected)
        })
      },
      setSelected(selected) {
        const previousSelected = this.selected
        const newlySelected = selected.filter(item => !previousSelected.includes(item))
        const deselected = previousSelected.filter(item => !selected.includes(item))
        
        newlySelected.forEach(treatmentName => {
          this.$track(this.$analyticsEvents.MEDICATION_SELECTED, {
            component: 'Treatment',
            medication_name: treatmentName,
            total_selected: selected.length
          })
        })
        
        deselected.forEach(treatmentName => {
          this.$track(this.$analyticsEvents.MEDICATION_SELECTED, {
            component: 'Treatment',
            medication_name: treatmentName,
            action: 'deselected',
            total_selected: selected.length
          })
        })
        
        this.selected = selected
        this.$nextTick(() => {
          this.updateDisabledItems();
        });
      },
      updateDisabledItems() {
        const currentDisablingFlags = new Set();
        const selectedTreatments = this.treatments(this.selected);

        selectedTreatments.forEach(treatment => {
          if (treatment.treatmentFlags) {
            treatment.treatmentFlags.forEach(flag => {
              currentDisablingFlags.add(flag.name)
            });
          }
        });

        // Set the disabled state based on shared treatment flags
        this.treatments().forEach(item => {
          if (!item.treatmentFlags) return;
          const disablingFlag = item.treatmentFlags.find(flag => {
            // current selected item is excluded so that it doesn't disable itself
            return currentDisablingFlags.has(flag.name) && !this.selected.includes(item.name)
          });

          // Store the disabled state and the token for the tooltip
          if (disablingFlag) {
            this.$set(this.disabled, item.name, {
              token: 'app.treatments.instructions.tooltip.options'
            });
          } else {
            this.$delete(this.disabled, item.name);
          }
        });
      },
      getTreatmentsForAssessment(assessment) {
        return this.treatments().filter(b => b.assessment === assessment)
      },
      formatTreatments(assessment) {
        const treatmentsForAssessment = this.getTreatmentsForAssessment(assessment)
        const formatted = treatmentsForAssessment.map(treatment => {
          const headerToken = `treatment.${treatment.name}`
          const content = this.convertTreatmentToString(treatment)
          return {
              detailContent: [content], 
              headerToken: headerToken,
              name: treatment.name,
              treatmentFlags: treatment.treatmentFlags,
              useHTMLFormat: true
          }
        })
        return formatted
      }
    }
  }
</script>

<style scoped>
  .treatment-instructions {
    background-color: transparent;
    color: white;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
  }
</style>
